import { useId } from 'react';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import type { AddressChoice, E911Address, NumberOrder, OrderLine, OrderStatus, SavedAddress } from '../api/client';
import type { Tone } from '../lib/format';
import { Icon } from '../lib/icons';
import { Chip, Field } from './ui';

/** The states (and DC and Puerto Rico) a 911 address can be in. */
export const STATES: { value: string; label: string }[] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['PR', 'Puerto Rico'],
  ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming']
].map(([value, label]) => ({ value: value!, label: label! }));

export const EMPTY_ADDRESS: E911Address = { address1: '', address2: '', city: '', state: '', zip: '' };

/** "100 Light St, Suite 200, Baltimore, MD 21202". */
export function oneLine(a: E911Address): string {
  return [a.address1, a.address2, a.city, `${a.state} ${a.zip}`.trim()].filter((x) => x && x.trim()).join(', ');
}

export const isTollFree = (n: string) => /^\+18(00|33|44|55|66|77|88)\d{7}$/.test(n);

/** The same checks the server makes, so mistakes show next to the field. */
export function addressErrors(a: E911Address): Partial<Record<keyof E911Address, string>> {
  const e: Partial<Record<keyof E911Address, string>> = {};
  if (a.address1.trim().length < 3) e.address1 = 'Enter the street address, like 100 Light St.';
  if (a.city.trim().length < 2) e.city = 'Enter the city.';
  if (!a.state) e.state = 'Choose the state.';
  if (!/^\d{5}(-\d{4})?$/.test(a.zip.trim())) e.zip = 'A ZIP code like 21202.';
  return e;
}

/** Street, where in the building, city, state, ZIP. */
export function AddressFields({ value, onChange, errors = {}, autoFocus }: { value: E911Address; onChange: (a: E911Address) => void; errors?: Partial<Record<keyof E911Address, string>>; autoFocus?: boolean }) {
  const set = (p: Partial<E911Address>) => onChange({ ...value, ...p });
  const stateId = useId();
  return (
    <div className="address-fields">
      <Field label="Street address" value={value.address1} onChange={(e) => set({ address1: e.target.value })} error={errors.address1} autoComplete="address-line1" autoFocus={autoFocus} />
      <Field
        label="Building, floor or suite"
        value={value.address2}
        onChange={(e) => set({ address2: e.target.value })}
        hint="Where in the building, so responders find people quickly. Like “Floor 3, Suite 300”."
        autoComplete="address-line2"
      />
      <div className="address-row">
        <Field label="City" value={value.city} onChange={(e) => set({ city: e.target.value })} error={errors.city} autoComplete="address-level2" />
        <div className="field">
          <label htmlFor={stateId}>State</label>
          <select id={stateId} className="input" value={value.state} onChange={(e) => set({ state: e.target.value })} aria-invalid={errors.state ? true : undefined} autoComplete="address-level1">
            <option value="">Choose</option>
            {STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {errors.state && <span className="error">{errors.state}</span>}
        </div>
        <Field label="ZIP code" inputMode="numeric" value={value.zip} onChange={(e) => set({ zip: e.target.value })} error={errors.zip} autoComplete="postal-code" />
      </div>
    </div>
  );
}

/** What someone chose for a 911 address: a saved one, a new one (maybe saved too), or none yet. */
export type AddressDraft = { mode: 'saved'; id: string } | { mode: 'new'; address: E911Address; save: boolean; label: string } | { mode: 'none' };

export function draftFor(saved: SavedAddress[]): AddressDraft {
  return saved[0] ? { mode: 'saved', id: saved[0].id } : { mode: 'new', address: { ...EMPTY_ADDRESS }, save: true, label: '' };
}

/** Problems with the choice, field by field ("label" for the name of a saved address). */
export function draftErrors(d: AddressDraft): Partial<Record<keyof E911Address | 'label' | 'saved', string>> {
  if (d.mode === 'none') return {};
  if (d.mode === 'saved') return d.id ? {} : { saved: 'Choose an address.' };
  const e: Partial<Record<keyof E911Address | 'label', string>> = addressErrors(d.address);
  if (d.save && !d.label.trim()) e.label = 'Name it, like “Main office”.';
  return e;
}

export function choiceOf(d: AddressDraft): AddressChoice | null {
  if (d.mode === 'none') return null;
  if (d.mode === 'saved') return { addressId: d.id };
  const a = { ...d.address, address1: d.address.address1.trim(), address2: d.address.address2.trim(), city: d.address.city.trim(), zip: d.address.zip.trim() };
  return d.save && d.label.trim() ? { ...a, saveAs: d.label.trim() } : a;
}

/** The address a draft stands for, to show back before it's sent. */
export function draftAddress(d: AddressDraft, saved: SavedAddress[]): (E911Address & { label?: string }) | null {
  if (d.mode === 'none') return null;
  if (d.mode === 'saved') return saved.find((s) => s.id === d.id) ?? null;
  return { ...d.address, ...(d.save && d.label.trim() ? { label: d.label.trim() } : {}) };
}

/**
 * Choosing a 911 address: one of the company's saved addresses, or a new one (saved for next time
 * unless they say not to). `none` offers leaving it for now, with what that means.
 */
export function AddressChooser({
  saved,
  value,
  onChange,
  showErrors,
  none
}: {
  saved: SavedAddress[];
  value: AddressDraft;
  onChange: (d: AddressDraft) => void;
  showErrors?: boolean;
  /** Offer "not now", with this explanation. */
  none?: string;
}) {
  const errors = showErrors ? draftErrors(value) : {};
  const group = useId();
  const fresh = value.mode === 'new' ? value : { mode: 'new' as const, address: { ...EMPTY_ADDRESS }, save: true, label: '' };
  return (
    <fieldset className="plain-fieldset">
      <legend className="sr-only">911 address</legend>
      {saved.map((a) => (
        <label className="radio-card" key={a.id}>
          <input type="radio" name={group} checked={value.mode === 'saved' && value.id === a.id} onChange={() => onChange({ mode: 'saved', id: a.id })} />
          <span className="cell-stack">
            <strong>{a.label}</strong>
            <small>{oneLine(a)}</small>
          </span>
        </label>
      ))}
      <label className="radio-card">
        <input type="radio" name={group} checked={value.mode === 'new'} onChange={() => onChange(fresh)} />
        <span className="cell-stack">
          <strong>{saved.length ? 'A different address' : 'Enter the address'}</strong>
          <small>Where people using the number are: the building, and where in it.</small>
        </span>
      </label>
      {value.mode === 'new' && (
        <div className="address-new">
          <AddressFields value={value.address} onChange={(address) => onChange({ ...value, address })} errors={errors} />
          <label className="check">
            <input type="checkbox" checked={value.save} onChange={(e) => onChange({ ...value, save: e.target.checked })} />
            Save it for next time
          </label>
          {value.save && <Field label="Name for this address" value={value.label} onChange={(e) => onChange({ ...value, label: e.target.value })} error={errors.label} placeholder="Main office" maxLength={60} />}
        </div>
      )}
      {none && (
        <label className="radio-card">
          <input type="radio" name={group} checked={value.mode === 'none'} onChange={() => onChange({ mode: 'none' })} />
          <span className="cell-stack">
            <strong>Not now</strong>
            <small>{none}</small>
          </span>
        </label>
      )}
      {errors.saved && <span className="error">{errors.saved}</span>}
    </fieldset>
  );
}

export const ORDER_STATUS: Record<OrderStatus, { label: string; tone: Tone }> = {
  working: { label: 'Working on it', tone: 'brand' },
  done: { label: 'Ready', tone: 'ok' },
  partial: { label: 'Partly done', tone: 'warn' },
  failed: { label: 'Didn’t go through', tone: 'crit' },
  needs_staff: { label: 'Stitch is finishing', tone: 'neutral' },
  porting: { label: 'Moving in', tone: 'brand' },
  cancelled: { label: 'Cancelled', tone: 'neutral' }
};

/** How one number in an order is getting on, in words. */
export function lineState(l: OrderLine, order: Pick<NumberOrder, 'kind' | 'status'>): { text: string; tone: Tone } {
  if (!l.bought) {
    if (order.kind === 'port') return order.status === 'cancelled' ? { text: 'Not moving', tone: 'neutral' } : { text: 'Waiting for the move', tone: 'brand' };
    return l.error ? { text: l.error, tone: 'crit' } : { text: 'Getting it', tone: 'brand' };
  }
  if (l.pbx === 'failed' || l.e911 === 'failed') return { text: 'Stitch support is looking at it', tone: 'warn' };
  if (l.pbx === 'waiting') return { text: 'Stitch is connecting it', tone: 'neutral' };
  if (l.pbx === 'pending' || l.e911 === 'pending') return { text: 'Setting it up', tone: 'brand' };
  return { text: 'Ready', tone: 'ok' };
}

/** Each number in an order with its three steps: got it, 911 address, ready to take calls. */
export function OrderLines({ order, staff }: { order: NumberOrder; staff?: boolean }) {
  return (
    <>
      {/* After the order errored: what the carrier's own list showed, in plain words (3125). */}
      {order.carrierCheck && (
        <p className={`carrier-check ${order.carrierCheck.outcome}`} role="status">
          <Icon name={order.carrierCheck.outcome === 'unknown' ? 'alert' : order.carrierCheck.outcome === 'nothing' ? 'info' : 'check'} size={15} />
          <span>{order.carrierCheck.said}</span>
        </p>
      )}
      <OrderLineList order={order} staff={staff} />
    </>
  );
}

function OrderLineList({ order, staff }: { order: NumberOrder; staff?: boolean | undefined }) {
  return (
    <ul className="order-lines">
      {order.lines.map((l) => {
        const s = lineState(l, order);
        return (
          <li key={l.number}>
            <span className="mono">{formatPhoneNumber(l.number)}</span>
            <span className="order-steps" aria-label={`${formatPhoneNumber(l.number)}: ${s.text}`}>
              <StepMark done={l.bought} failed={!l.bought && Boolean(l.error)} label={order.kind === 'port' ? 'Moved' : 'Got it'} />
              <StepMark done={l.e911 === 'set'} failed={l.e911 === 'failed'} skipped={l.e911 === 'skipped' && l.bought} label="911 address" />
              <StepMark done={l.pbx === 'added'} failed={l.pbx === 'failed' && l.bought} label="Takes calls" />
            </span>
            <Chip small tone={s.tone}>
              {s.text}
            </Chip>
            {staff && l.error && <small className="muted order-error">{l.error}</small>}
          </li>
        );
      })}
    </ul>
  );
}

function StepMark({ done, failed, skipped, label }: { done: boolean; failed?: boolean; skipped?: boolean; label: string }) {
  const state = done ? 'done' : failed ? 'failed' : skipped ? 'skipped' : 'todo';
  return (
    <span className={`step-mark ${state}`} title={`${label}: ${state === 'todo' ? 'not yet' : state === 'skipped' ? 'not set' : state}`}>
      {done ? <Icon name="check" size={12} strokeWidth={2.8} /> : failed ? <Icon name="x" size={12} strokeWidth={2.8} /> : null}
      <span>{label}</span>
    </span>
  );
}
